/**
 * Pure date helpers for the Reports & Analytics date-range filter (prototype).
 * All functions are deterministic given their inputs — no `new Date()` side
 * effects inside the math, so they are trivially unit-testable.
 */

export interface DateRange {
  start: Date
  end: Date
}

export type DateRangePresetId =
  'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom'

export const dateRangePresets: { id: DateRangePresetId; label: string }[] = [
  { id: 'today', label: 'Hari ini' },
  { id: 'yesterday', label: 'Kemarin' },
  { id: 'last7', label: '7 hari terakhir' },
  { id: 'last30', label: '30 hari terakhir' },
  { id: 'thisMonth', label: 'Bulan ini' },
  { id: 'lastMonth', label: 'Bulan lalu' },
  { id: 'custom', label: 'Kustom' },
]

/** Midnight of the given date (strips the time part for day-level comparisons). */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function addDays(date: Date, days: number): Date {
  const next = startOfDay(date)
  next.setDate(next.getDate() + days)
  return next
}

/** Inclusive day count between two dates (same day = 1). */
export function rangeLengthDays(range: DateRange): number {
  const ms = startOfDay(range.end).getTime() - startOfDay(range.start).getTime()
  return Math.floor(ms / 86_400_000) + 1
}

/** Resolve a preset to a concrete range, relative to `today`. */
export function presetRange(id: DateRangePresetId, today: Date): DateRange | null {
  const base = startOfDay(today)
  switch (id) {
    case 'today':
      return { start: base, end: base }
    case 'yesterday': {
      const d = addDays(base, -1)
      return { start: d, end: d }
    }
    case 'last7':
      return { start: addDays(base, -6), end: base }
    case 'last30':
      return { start: addDays(base, -29), end: base }
    case 'thisMonth':
      return { start: new Date(base.getFullYear(), base.getMonth(), 1), end: base }
    case 'lastMonth': {
      const start = new Date(base.getFullYear(), base.getMonth() - 1, 1)
      const end = new Date(base.getFullYear(), base.getMonth(), 0)
      return { start, end }
    }
    case 'custom':
      return null
  }
}

/** Which preset (if any) exactly matches the given range, relative to `today`. */
export function matchPreset(range: DateRange, today: Date): DateRangePresetId {
  for (const { id } of dateRangePresets) {
    if (id === 'custom') continue
    const preset = presetRange(id, today)
    if (preset && sameDay(preset.start, range.start) && sameDay(preset.end, range.end)) return id
  }
  return 'custom'
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function isWithin(day: Date, range: DateRange): boolean {
  const t = startOfDay(day).getTime()
  return t >= startOfDay(range.start).getTime() && t <= startOfDay(range.end).getTime()
}

const shortMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Mei',
  'Jun',
  'Jul',
  'Agu',
  'Sep',
  'Okt',
  'Nov',
  'Des',
]
const longMonths = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
]

/** "23 Okt – 29 Okt 2026" (drops the year on the start when both share it). */
export function formatRangeLabel(range: DateRange): string {
  const s = range.start
  const e = range.end
  const startText =
    s.getFullYear() === e.getFullYear()
      ? `${s.getDate()} ${shortMonths[s.getMonth()]}`
      : `${s.getDate()} ${shortMonths[s.getMonth()]} ${s.getFullYear()}`
  const endText = `${e.getDate()} ${shortMonths[e.getMonth()]} ${e.getFullYear()}`
  return sameDay(s, e) ? endText : `${startText} – ${endText}`
}

/**
 * Compact range text: "23–29 Okt 2026" (same month), "23 Sep – 5 Okt 2026"
 * (same year), "29 Des 2025 – 4 Jan 2026" (across years), "29 Okt 2026" (one day).
 */
export function formatRangeCompact(range: DateRange): string {
  const s = range.start
  const e = range.end
  const endText = `${e.getDate()} ${shortMonths[e.getMonth()]} ${e.getFullYear()}`
  if (sameDay(s, e)) return endText
  if (s.getFullYear() !== e.getFullYear()) {
    return `${s.getDate()} ${shortMonths[s.getMonth()]} ${s.getFullYear()} – ${endText}`
  }
  if (s.getMonth() !== e.getMonth()) {
    return `${s.getDate()} ${shortMonths[s.getMonth()]} – ${endText}`
  }
  return `${s.getDate()}–${endText}`
}

/** Month-level range text: "Mei–Okt 2026", or "Nov 2025 – Apr 2026" across years. */
export function formatMonthSpan(range: DateRange): string {
  const s = range.start
  const e = range.end
  const endText = `${shortMonths[e.getMonth()]} ${e.getFullYear()}`
  if (s.getFullYear() !== e.getFullYear()) {
    return `${shortMonths[s.getMonth()]} ${s.getFullYear()} – ${endText}`
  }
  return s.getMonth() === e.getMonth() ? endText : `${shortMonths[s.getMonth()]}–${endText}`
}

/** "7 hari terakhir · 23–29 Okt 2026"; custom ranges use the day count ("12 hari · …"). */
export function formatRangeCaption(range: DateRange, today: Date): string {
  const presetId = matchPreset(range, today)
  const presetLabel =
    presetId === 'custom'
      ? `${rangeLengthDays(range)} hari`
      : (dateRangePresets.find((preset) => preset.id === presetId)?.label ?? '')
  return `${presetLabel} · ${formatRangeCompact(range)}`
}

/** "23/10/2026" for the footer inputs. */
export function formatInputDate(date: Date): string {
  const d = String(date.getDate()).padStart(2, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  return `${d}/${m}/${date.getFullYear()}`
}

export function monthTitle(year: number, month: number): string {
  return `${longMonths[month]} ${year}`
}

/** Weekday labels, Monday-first (design order: Sen..Min). */
export const weekdayLabels = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min']

/**
 * The 1-based day numbers for a month, padded with leading nulls so the first
 * day lands on the correct Monday-first weekday column.
 */
export function monthGrid(year: number, month: number): (number | null)[] {
  const first = new Date(year, month, 1)
  // JS getDay(): 0=Sun..6=Sat → Monday-first offset.
  const leading = (first.getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: (number | null)[] = Array.from({ length: leading }, () => null)
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d)
  return cells
}

/** Add `delta` months to a {year, month} pair, normalizing overflow. */
export function shiftMonth(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const base = new Date(year, month + delta, 1)
  return { year: base.getFullYear(), month: base.getMonth() }
}
